import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const PAY_STATUS = {
  success: "bg-primary/15 text-primary border-primary/30",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
  created: "bg-warning/15 text-warning border-warning/30",
  pending: "bg-warning/15 text-warning border-warning/30",
};

export default function AdminBilling() {
  const [payPage, setPayPage] = useState(1);
  const [subPage, setSubPage] = useState(1);
  const [auditPage, setAuditPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");

  const payments = useQuery({
    queryKey: ["admin-payments", payPage, statusFilter],
    queryFn: async () => (await api.get("/billing/admin/payments", { params: { page: payPage, page_size: 12, status: statusFilter === "all" ? "" : statusFilter } })).data,
  });
  const subs = useQuery({ queryKey: ["admin-subs", subPage], queryFn: async () => (await api.get("/billing/admin/subscriptions", { params: { page: subPage, page_size: 12 } })).data });
  const audit = useQuery({ queryKey: ["admin-audit", auditPage], queryFn: async () => (await api.get("/billing/admin/audit", { params: { page: auditPage, page_size: 15 } })).data });

  return (
    <div className="space-y-6" data-testid="admin-billing-page">
      <PageHeader title="Billing & Revenue" subtitle="Monitor all subscriptions, payments and plan changes" breadcrumb={["Platform", "Billing"]} />
      <Tabs defaultValue="payments">
        <TabsList data-testid="admin-billing-tabs">
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Upgrade / Downgrade History</TabsTrigger>
        </TabsList>

        <TabsContent value="payments">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">All Payments</CardTitle>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPayPage(1); }}>
                <SelectTrigger className="w-40" data-testid="payment-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="created">Pending</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Center</TableHead><TableHead>Owner</TableHead><TableHead>Plan</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Razorpay Payment ID</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {payments.isLoading
                      ? Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-8" /></TableCell></TableRow>)
                      : (payments.data?.items || []).map((p) => (
                          <TableRow key={p.id} data-testid={`admin-payment-${p.id}`}>
                            <TableCell className="text-xs">{(p.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                            <TableCell>{p.center_name}</TableCell>
                            <TableCell className="text-sm">{p.owner_name}</TableCell>
                            <TableCell>{p.plan_name} <span className="text-xs text-muted-foreground capitalize">/{p.billing_cycle}</span></TableCell>
                            <TableCell className="text-right tnum">{money(p.amount)}</TableCell>
                            <TableCell><span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${PAY_STATUS[p.status] || ""}`}>{p.status}</span>{p.failure_reason && <p className="text-[11px] text-destructive">{p.failure_reason}</p>}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{p.razorpay_payment_id || "—"}</TableCell>
                          </TableRow>
                        ))}
                    {!payments.isLoading && payments.data?.items?.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No payments found.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <TablePagination page={payPage} pages={payments.data?.pages} total={payments.data?.total} onPage={setPayPage} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscriptions">
          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">All Subscriptions</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Center</TableHead><TableHead>Owner</TableHead><TableHead>Plan</TableHead><TableHead>Cycle</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Renews</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {subs.isLoading
                      ? Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-8" /></TableCell></TableRow>)
                      : (subs.data?.items || []).map((s) => (
                          <TableRow key={s.id} data-testid={`admin-sub-${s.id}`}>
                            <TableCell>{s.center_name}</TableCell>
                            <TableCell className="text-sm">{s.owner_name}<br /><span className="text-xs text-muted-foreground">{s.owner_email}</span></TableCell>
                            <TableCell>{s.plan_name}</TableCell>
                            <TableCell className="capitalize">{s.billing_cycle}</TableCell>
                            <TableCell className="text-right tnum">{money(s.amount)}</TableCell>
                            <TableCell className="text-xs">{(s.current_period_end || "").slice(0, 10)}</TableCell>
                            <TableCell><Badge variant={s.status === "active" ? "default" : "secondary"} className="capitalize">{s.status}</Badge></TableCell>
                          </TableRow>
                        ))}
                    {!subs.isLoading && subs.data?.items?.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No subscriptions yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <TablePagination page={subPage} pages={subs.data?.pages} total={subs.data?.total} onPage={setSubPage} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">Plan Change History</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Center</TableHead><TableHead>Action</TableHead><TableHead>From → To</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {audit.isLoading
                      ? Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-8" /></TableCell></TableRow>)
                      : (audit.data?.items || []).map((a) => (
                          <TableRow key={a.id} data-testid={`admin-audit-${a.id}`}>
                            <TableCell className="text-xs">{(a.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                            <TableCell>{a.center_name}</TableCell>
                            <TableCell><Badge variant="outline" className="capitalize">{a.action}</Badge></TableCell>
                            <TableCell className="text-sm">{a.from_plan || "—"}{a.from_cycle ? ` (${a.from_cycle})` : ""} → {a.to_plan || "—"}{a.to_cycle ? ` (${a.to_cycle})` : ""}</TableCell>
                            <TableCell className="text-right tnum">{money(a.amount)}</TableCell>
                          </TableRow>
                        ))}
                    {!audit.isLoading && audit.data?.items?.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No plan changes yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <TablePagination page={auditPage} pages={audit.data?.pages} total={audit.data?.total} onPage={setAuditPage} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
